import jwt_decode from 'jwt-decode';
import React, { useEffect } from 'react';
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
  useNavigate
} from 'react-router-dom';

import HeaderBar from './components/HeaderBar';
import ErrorBoundary from './components/common/ErrorBoundary';
import DashboardPage from './pages/DashboardPage';
import AppReleasesPage from './pages/AppReleasesPage';
import DeliveriesPage from './pages/DeliveriesPage';
import DeliveryDetailsPage from './pages/DeliveryDetailsPage';
import LoginPage from './pages/LoginPage';
import PackageDetailsPage from './pages/PackageDetailsPage';
import PackageManagerPage from './pages/PackageManagerPage';
import ProfilePage from './pages/ProfilePage';
import PurchaseOrdersPage from './pages/PurchaseOrdersPage';
import ReportsPage from './pages/ReportsPage';
import ZoeOrderDetailsPage from './pages/ZoeOrderDetailsPage';
import ZoeOrdersPage from './pages/ZoeOrdersPage';
import ZoeShipmentTrackingPage from './pages/ZoeShipmentTrackingPage';

import ChangeLogsPage from './pages/ChangeLogsPage';
import PackagesByDatePage from './pages/PackagesByDatePage';
import PurchaseOrderDetailsPage from './pages/PurchaseOrderDetailsPage';
import UserRoleManagementPage from './pages/UserRoleManagementPage';
import ZoeShipmentTrackingDetailsPage from './pages/ZoeShipmentTrackingDetailsPage';
import ZoeShipmentTrackingLogPage from './pages/ZoeShipmentTrackingLogPage';
import ZoeShipmentsByDatePage from './pages/ZoeShipmentsByDatePage';
import ZoeShipmentsByStatusPage from './pages/ZoeShipmentsByStatusPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import NotFoundPage from './pages/NotFoundPage';
import { storageHandler } from './utils/storage_handler';

// Hoisted so the arrays keep a stable identity across renders. Passed inline
// as literals they were a new reference every render, re-firing PrivateRoute's
// effect each time.
const SUPERADMIN_ONLY = [1];
const ADMIN_AND_SUPERADMIN = [2, 1];

/**
 * Decide whether the current session may view a route.
 *
 * jwt_decode throws on a malformed token, which previously propagated out of
 * the effect and hit the ErrorBoundary; treat an unparseable token as simply
 * not authenticated.
 */
function resolveAccess(token, usertype, allowedUsertypes) {
  if (!token) return { allowed: false, reason: 'unauthenticated' };

  let decoded;
  try {
    decoded = jwt_decode(token);
  } catch {
    return { allowed: false, reason: 'expired' };
  }

  if (!decoded.exp || decoded.exp <= Date.now() / 1000) {
    return { allowed: false, reason: 'expired' };
  }
  if (allowedUsertypes && !allowedUsertypes.includes(usertype)) {
    return { allowed: false, reason: 'forbidden' };
  }
  return { allowed: true };
}

function PrivateRoute({ children, allowedUsertypes }) {
  const navigate = useNavigate();
  const token = storageHandler.localStorageGet('token');
  const usertype = parseInt(storageHandler.localStorageGet('usertype'), 10);

  const { allowed, reason } = resolveAccess(token, usertype, allowedUsertypes);

  useEffect(() => {
    if (allowed) return;

    if (reason === 'expired') {
      storageHandler.localStorageDelete('token');
      storageHandler.localStorageDelete('usertype');
    }
    navigate(reason === 'forbidden' ? '/' : '/login');
  }, [allowed, reason, navigate]);

  // Render nothing until the guard passes. Returning `children`
  // unconditionally mounted the protected page and fired its data-fetch
  // effects with a missing, expired, or wrong-role token — so an unauthorised
  // user could briefly see real content, and expired sessions raced a hard
  // redirect from api_fetch's 401 handler against this one.
  if (!allowed) return null;

  return children;
}

function App() {
  return (
    <ErrorBoundary>
      <Router>
        <HeaderBar />
        <ErrorBoundary>
          <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route
          path="/profile"
          element={
            <PrivateRoute>
              <ProfilePage />
            </PrivateRoute>
          }
        />
        <Route
          path="/"
          element={
            <PrivateRoute>
              <DashboardPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/packages"
          element={
            <PrivateRoute>
              <PackageManagerPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/deliveries"
          element={
            <PrivateRoute>
              <DeliveriesPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/delivery-details/:id"
          element={
            <PrivateRoute>
              <DeliveryDetailsPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/zoe-shipment-tracking"
          element={
            <PrivateRoute>
              <ZoeShipmentTrackingPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/purchase-orders"
          element={
            <PrivateRoute>
              <PurchaseOrdersPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/purchase-orders/:id"
          element={
            <PrivateRoute>
              <PurchaseOrderDetailsPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/zoe-shipment-tracking-logs"
          element={
            <PrivateRoute allowedUsertypes={ADMIN_AND_SUPERADMIN}>
              <ZoeShipmentTrackingLogPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/user-role-management"
          element={
            <PrivateRoute allowedUsertypes={SUPERADMIN_ONLY}>
              <UserRoleManagementPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/zoe-shipment-tracking/:id"
          element={
            <PrivateRoute>
              <ZoeShipmentTrackingDetailsPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/zoe-shipments-status/:status"
          element={
            <PrivateRoute>
              <ZoeShipmentsByStatusPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/zoe-shipments-date/:dateType"
          element={
            <PrivateRoute>
              <ZoeShipmentsByDatePage />
            </PrivateRoute>
          }
        />
        <Route
          path="/packages-date/:dateType"
          element={
            <PrivateRoute>
              <PackagesByDatePage />
            </PrivateRoute>
          }
        />
        <Route
          path="/package-details/:id"
          element={
            <PrivateRoute>
              <PackageDetailsPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/change-logs"
          element={
            <PrivateRoute allowedUsertypes={SUPERADMIN_ONLY}>
              <ChangeLogsPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/reports"
          element={
            <PrivateRoute>
              <ReportsPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/zoe-orders"
          element={
            <PrivateRoute>
              <ZoeOrdersPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/zoe-orders/:id"
          element={
            <PrivateRoute>
              <ZoeOrderDetailsPage />
            </PrivateRoute>
          }
        />
        <Route
          path="/app-releases"
          element={
            <PrivateRoute allowedUsertypes={[1]}>
              <AppReleasesPage />
            </PrivateRoute>
          }
        />
        {/* Legacy iOS-only page superseded by the unified App Releases screen.
            The redirect target is itself wrapped in PrivateRoute, so this keeps
            the logged-out gating that the security fix added to this path. */}
        <Route
          path="/ipa-distribution"
          element={<Navigate to="/app-releases" replace />}
        />

        <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </ErrorBoundary>
      </Router>
    </ErrorBoundary>
  );
}

export default App;
